/**
 * awe-announcement-timer-bar.js
 *
 * Custom element for the awe-announcement-timer-bar section.
 *
 * The section only renders (server-side) while now is between Start Date and
 * End Date. This element just ticks the countdown every second and, if End
 * Date passes while the page stays open, fades the bar out and removes it —
 * no reload required.
 *
 * Data attributes (set by Liquid):
 *   data-end — End Date as a Unix timestamp in milliseconds
 */

class AweAnnouncementTimerBar extends HTMLElement {
  /** @type {number|null} */
  #intervalId = null;

  /** @type {number} */
  #endTime = 0;

  /** @type {HTMLElement|null} */
  #valueEl = null;

  connectedCallback() {
    this.#endTime = Number(this.dataset.end) || 0;
    this.#valueEl = this.querySelector('[data-countdown-value]');

    if (!this.#endTime || !this.#valueEl) return;

    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #tick() {
    const distance = this.#endTime - Date.now();

    if (distance <= 0) {
      this.#expire();
      return;
    }

    const days = Math.floor(distance / 86400000);
    const hours = Math.floor((distance % 86400000) / 3600000);
    const minutes = Math.floor((distance % 3600000) / 60000);
    const seconds = Math.floor((distance % 60000) / 1000);

    let formatted = `${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
    if (days > 0) formatted = `${days}d ${formatted}`;

    if (this.#valueEl) this.#valueEl.textContent = formatted;
  }

  #expire() {
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }

    const bar = this.closest('.awe-announcement-timer-bar');
    const target = bar ?? this;

    target.classList.add('awe-announcement-timer-bar--hidden');
    window.setTimeout(() => {
      target.remove();
    }, 300);
  }
}

if (!customElements.get('awe-announcement-timer-bar-component')) {
  customElements.define('awe-announcement-timer-bar-component', AweAnnouncementTimerBar);
}
